import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SiteScaffoldBrandChoice } from '../../types';
import {
  BRAND_OWNED_LABELS,
  BUTTON_EMPHASES,
  LINK_LABEL_MAX,
  LINK_URL_MAX,
  SPEC_GROUPS,
  SpecField,
  SpecLink,
  coerceFieldValue,
  dottedGet,
  dottedSet,
  fontOptions,
  isAtDefault,
  isHex,
  luminance,
  newButton,
  newLink,
} from './siteSpec';

/**
 * The design, as fields.
 *
 * WHY THIS EXISTS. Everything the agent decides has always been a typed field —
 * `site.title`, the seven palette colours, the header background — but the
 * studio showed none of them, so the only way to change a colour was to ask for
 * it in prose and the only way to see what the agent had chosen was to look at
 * the rendered page and guess. Watching the fields move as the agent works is
 * most of what makes it legible.
 *
 * Edits are LOCAL until they settle. Every keystroke saving a version would
 * bury the version list under one entry per character; instead the panel keeps
 * a draft, previews it immediately (the preview endpoint is a pure compile, so
 * it is free), and saves on blur or after a pause.
 */
export default function SiteSpecPanel({
  spec,
  defaults,
  brandProfiles,
  brandProfileId,
  disabled,
  active = true,
  focusPath,
  onFocusConsumed,
  onDraft,
  onCommit,
  onBrandChange,
  onUnseenChange,
}: {
  spec: any;
  /** What each field reverts to. From the server, so it cannot drift. */
  defaults: any;
  brandProfiles: SiteScaffoldBrandChoice[];
  brandProfileId: number | null;
  disabled?: boolean;
  /** Is this panel the one on screen? Drives whether marks fade or accumulate. */
  active?: boolean;
  /** Scroll to this field and mark it — set when arriving from a change chip. */
  focusPath?: string | null;
  /** The mark has been seen; drop `focusPath`. */
  onFocusConsumed?: () => void;
  /** Unsaved — repaint the preview from this. */
  onDraft: (spec: any) => void;
  /** Settled — persist it. */
  onCommit: (spec: any) => void;
  onBrandChange: (id: number | null, apply: boolean) => void;
  /** How many fields have moved since this panel was last looked at. */
  onUnseenChange?: (count: number) => void;
}) {
  const [draft, setDraft] = useState<any>(spec);
  const dirty = useRef(false);
  const previous = useRef<any>(spec);
  // Paths the AGENT just moved. This is most of why the panel is worth having:
  // watching a colour change under you as the agent works is what makes what it
  // did legible, and a value that simply appears with no signal reads as though
  // the panel had always said that.
  const [justChanged, setJustChanged] = useState<Set<string>>(new Set());

  // The agent's edits arrive as a new `spec` prop. They must win — but only
  // when the user is not mid-edit, or a save landing while someone is typing
  // yanks the field out from under them.
  useEffect(() => {
    if (dirty.current) return;
    const moved = changedPaths(previous.current, spec);
    previous.current = spec;
    latest.current = spec;
    setDraft(spec);
    if (!moved.size) return;
    // OFF SCREEN, THE MARKS ACCUMULATE AND DO NOT FADE. A six-second highlight
    // is the right length for a mark you are looking at and useless for one on
    // a tab you are not — the agent's edits would land, fade, and be gone
    // before you ever opened the panel. So while this is hidden they pile up,
    // and the fade only starts once it is on screen.
    // `Array.from` rather than spreading a Set — this project's tsconfig
    // targets ES5 and iterating a Set directly does not compile.
    setJustChanged(
      (current) => new Set(Array.from(current).concat(Array.from(moved))),
    );
  }, [spec]);

  useEffect(() => {
    if (!active || !justChanged.size) return;
    const timer = setTimeout(() => setJustChanged(new Set()), 6000);
    return () => clearTimeout(timer);
  }, [active, justChanged]);

  useEffect(() => {
    onUnseenChange?.(active ? 0 : justChanged.size);
  }, [active, justChanged, onUnseenChange]);

  // Arriving from a change chip: bring that field into view and mark it. The
  // panel is ~40 rows, so "it is in here somewhere" is not a useful answer.
  const rows = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (!active || !focusPath) return;
    const node = rows.current[focusPath];
    if (node?.scrollIntoView) node.scrollIntoView({ block: 'center' });
    // The ring says "here it is", not "this field is in a state". Nothing else
    // clears it, so it used to sit on that row for the rest of the session and
    // the next chip you followed put a second ring on screen beside it. Same
    // six seconds the change marks fade over.
    const timer = setTimeout(() => onFocusConsumed?.(), 6000);
    return () => clearTimeout(timer);
  }, [active, focusPath, onFocusConsumed]);

  // The user's own unsaved edits, marked the same way — in both cases the mark
  // means "this is not what is on the saved design".
  const pending = useMemo(() => changedPaths(spec, draft), [spec, draft]);

  // THE DRAFT IS ALSO HELD IN A REF, and the ref is what gets saved.
  //
  // A select and a checkbox have no "still typing" state, so they settle in the
  // same breath as they change. But `settle` closes over `draft` at render
  // time, so a settle fired from that same event handler saves the value the
  // field had BEFORE the change — the preview would repaint correctly from the
  // draft, the save would write the old value back, and the reload after it
  // would snap the preview back to where it started. Which reads exactly like
  // the control doing nothing.
  const latest = useRef<any>(spec);

  const write = (path: string, value: any) => {
    dirty.current = true;
    const next = dottedSet(latest.current, path, value);
    latest.current = next;
    setDraft(next);
    onDraft(next);
  };

  const edit = (field: SpecField, raw: any) => {
    const value = coerceFieldValue(field, raw);
    if (value === undefined) return;
    write(field.path, value);
  };

  /** Put one or more paths back to what the server says they default to. */
  const revert = (paths: string[]) => {
    paths.forEach((path) => write(path, dottedGet(defaults, path)));
    setTimeout(settle, 0);
  };

  const settle = () => {
    if (!dirty.current) return;
    dirty.current = false;
    onCommit(latest.current);
  };

  return (
    <div className="h-full overflow-y-auto text-sm">
      {/* SAYS WHAT THE TAB'S NUMBER MEANT, at the moment you act on it. A count
          on a tab is unreadable on its own — it could be anything — and a
          tooltip only explains it to someone who already suspected it was worth
          hovering. One line here teaches it once. */}
      {justChanged.size > 0 && (
        <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-[11px] text-blue-800 dark:text-blue-300 border-b border-blue-100 dark:border-blue-900">
          {justChanged.size} field{justChanged.size === 1 ? '' : 's'} just changed -
          marked in blue below.
        </div>
      )}
      <BrandRow
        profiles={brandProfiles}
        current={brandProfileId}
        disabled={disabled}
        onChange={onBrandChange}
      />
      {SPEC_GROUPS.map((group) => (
        <section key={group.title} className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-baseline justify-between gap-2 px-3 pt-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {group.title}
            </h3>
            <GroupReset
              fields={group.fields.filter(
                (f) => !isAtDefault(draft, defaults, f.path),
              )}
              disabled={disabled}
              onRevert={revert}
            />
          </div>
          {group.note && (
            <p className="px-3 pt-1 text-[11px] text-gray-500 dark:text-gray-400">{group.note}</p>
          )}
          <div className="px-3 py-2 space-y-2">
            {group.fields.map((field) => (
              <div
                key={field.path}
                ref={(node) => {
                  rows.current[field.path] = node;
                }}
                className={
                  focusPath === field.path
                    ? 'rounded ring-2 ring-blue-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
                    : undefined
                }
              >
              <Field
                key={field.path}
                field={field}
                spec={draft}
                value={dottedGet(draft, field.path)}
                disabled={disabled}
                recentlyChanged={pending.has(field.path) || justChanged.has(field.path)}
                atDefault={isAtDefault(draft, defaults, field.path)}
                onChange={(raw) => edit(field, raw)}
                onSettle={settle}
                onRevert={() => revert([field.path])}
              />
              </div>
            ))}
          </div>
        </section>
      ))}
      <p className="px-3 py-3 text-[11px] text-gray-400 dark:text-gray-500">
        Every change here is saved to Versions, so nothing you type is one-way.
      </p>
    </div>
  );
}

/**
 * Which of the panel's fields differ between two specs.
 *
 * COMPARED BY VALUE, not identity. Three of these fields — `nav.items`,
 * `nav.buttons`, `footer.links` — are arrays, and a refetched scaffold hands
 * back fresh ones every time. Under `!==` those three came back as "just
 * changed" on every single fetch, so finishing a chat round that touched
 * nothing still lit three rows and a "3 fields just changed" badge. The sibling
 * helpers in `siteSpec.ts` already compare this way.
 */
function changedPaths(before: any, after: any): Set<string> {
  const out = new Set<string>();
  SPEC_GROUPS.forEach((group) =>
    group.fields.forEach((field) => {
      const was = dottedGet(before, field.path);
      const now = dottedGet(after, field.path);
      if (was !== now && JSON.stringify(was ?? null) !== JSON.stringify(now ?? null)) {
        out.add(field.path);
      }
    }),
  );
  return out;
}

function BrandRow({
  profiles,
  current,
  disabled,
  onChange,
}: {
  profiles: SiteScaffoldBrandChoice[];
  current: number | null;
  disabled?: boolean;
  onChange: (id: number | null, apply: boolean) => void;
}) {
  if (!profiles.length) return null;
  const pick = (raw: string) => {
    const id = raw === '' ? null : Number(raw);
    const name = profiles.find((p) => p.id === id)?.name;
    const what = name
      ? `Repaint this design from ${name}?`
      : // Choosing no brand CLEARS these rather than leaving the last brand's
        // logo in the header with no brand selected.
        'Clear the brand from this design?';
    const apply = window.confirm(
      `${what}\n\nThis ${name ? 'changes' : 'resets'} ${BRAND_OWNED_LABELS.join(
        ', ',
      )}. Your layout, header shape, links and navigation stay as they are, and ` +
        `the current design is saved to Versions first.\n\nCancel to just ` +
        `remember the choice without changing anything.`,
    );
    onChange(id, apply);
  };
  return (
    <section className="border-b border-gray-200 dark:border-gray-700 px-3 py-3">
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
        Brand
      </label>
      <select
        value={current == null ? '' : String(current)}
        disabled={disabled}
        onChange={(e) => pick(e.target.value)}
        className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
      >
        <option value="">No particular brand</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.is_default ? ' (default)' : ''}
          </option>
        ))}
      </select>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        Which of your brands this site belongs to. A site is not part of a project, so
        nothing can work this out on its own.
      </p>
    </section>
  );
}

/**
 * Put a whole group back to its defaults, when anything in it has moved.
 *
 * SAYS "to defaults" IN THE BUTTON, not only in a tooltip. A bare "Reset 4" was
 * read as "undo the last four changes" — a completely different and much more
 * alarming action — and a count with no object is exactly the kind of label
 * that invites the wrong guess. The tooltip then names the fields, in the words
 * the panel uses for them rather than as dotted paths.
 */
function GroupReset({
  fields,
  disabled,
  onRevert,
}: {
  fields: SpecField[];
  disabled?: boolean;
  onRevert: (paths: string[]) => void;
}) {
  if (!fields.length) return null;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onRevert(fields.map((f) => f.path))}
      title={`Put back to their defaults: ${fields.map((f) => f.label).join(', ')}`}
      className="text-[10px] text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-30 whitespace-nowrap"
    >
      Reset {fields.length} to defaults
    </button>
  );
}

function Field({
  field,
  spec,
  value,
  disabled,
  recentlyChanged,
  atDefault,
  onChange,
  onSettle,
  onRevert,
}: {
  field: SpecField;
  /** For fields whose options depend on the design — the font lists. */
  spec: any;
  value: any;
  disabled?: boolean;
  recentlyChanged: boolean;
  atDefault: boolean;
  onChange: (raw: any) => void;
  onSettle: () => void;
  onRevert: () => void;
}) {
  // TWO DIFFERENT SIGNALS, deliberately not merged. The blue label means the
  // value moved JUST NOW — by you or by the agent — and fades. The revert arrow
  // means it is not at its default, which is a standing fact about the design.
  // Wearing one mark for both would make each of them unreadable.
  const label = (
    <span
      className={`text-xs ${
        recentlyChanged
          ? 'text-blue-600 dark:text-blue-400 font-medium'
          : 'text-gray-600 dark:text-gray-400'
      }`}
    >
      {field.label}
      {!atDefault && (
        <button
          type="button"
          disabled={disabled}
          onClick={onRevert}
          title="Put this back to its default"
          className="ml-1 align-middle text-[11px] text-gray-300 dark:text-gray-600 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-30"
        >
          ↺
        </button>
      )}
    </span>
  );

  const control = () => {
    if (field.kind === 'links' || field.kind === 'buttons') {
      // Full width and stacked: a row of label + url + remove has no chance
      // beside a label in a 19rem rail.
      return null;
    }
    if (field.kind === 'colour') {
      return (
        <ColourInput
          value={value}
          emptyLabel={field.emptyLabel}
          disabled={disabled}
          onChange={onChange}
          onSettle={onSettle}
        />
      );
    }
    if (field.kind === 'select') {
      const options = field.optionsFrom === 'fonts' ? fontOptions(spec) : field.options;
      return (
        <select
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            // A select has no meaningful "still typing" state, so it settles at
            // once rather than waiting for a blur that may never come.
            setTimeout(onSettle, 0);
          }}
          className="w-40 px-1.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          {(options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    if (field.kind === 'toggle') {
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.checked);
            setTimeout(onSettle, 0);
          }}
          className="h-4 w-4"
        />
      );
    }
    return (
      <input
        type={field.kind === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        min={field.min}
        max={field.max}
        step={field.path === 'home.hero_overlay' ? 0.05 : 1}
        placeholder={field.placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onSettle}
        className="w-40 px-1.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
      />
    );
  };

  const stacked = field.kind === 'links' || field.kind === 'buttons';

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        {label}
        {control()}
      </div>
      {field.hint && (
        <p className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">{field.hint}</p>
      )}
      {stacked && (
        <LinkRows
          value={value}
          buttons={field.kind === 'buttons'}
          maxItems={field.maxItems}
          disabled={disabled}
          onChange={onChange}
          onSettle={() => setTimeout(onSettle, 0)}
        />
      )}
    </div>
  );
}

/**
 * The label/url rows behind `nav.items`, `nav.buttons` and `footer.links`.
 *
 * ROWS ARE NEVER CREATED EMPTY. The backend's `Link` requires both fields, so a
 * blank new row would fail validation on every save until it was finished — and
 * a save that keeps being rejected reads as the panel not working. "Add" makes
 * a complete row you then edit in place.
 *
 * STRUCTURE settles immediately; TYPING settles on blur. Adding, removing,
 * reordering or restyling a row has no "still going" state and a row that
 * vanished because the save had not fired yet is the worst possible behaviour
 * here. A label being typed does have one — and settling on every keystroke
 * wrote a spec version per character, so typing "Documentation" into a nav
 * label buried the version list under thirteen entries (`record_spec_change`
 * only dedupes byte-identical specs). The text boxes therefore behave like
 * every other text field in this panel: repaint as you type, save on blur.
 */
function LinkRows({
  value,
  buttons,
  maxItems,
  disabled,
  onChange,
  onSettle,
}: {
  value: any;
  /** Buttons carry an emphasis; plain links do not. */
  buttons?: boolean;
  maxItems?: number;
  disabled?: boolean;
  onChange: (next: any) => void;
  onSettle: () => void;
}) {
  const rows: SpecLink[] = Array.isArray(value) ? (value as SpecLink[]) : [];

  const write = (next: SpecLink[], settle = true) => {
    onChange(next);
    if (settle) onSettle();
  };

  const setRow = (index: number, patch: Partial<SpecLink>, settle = true) =>
    write(
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
      settle,
    );

  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    [next[index], next[target]] = [next[target], next[index]];
    write(next);
  };

  const full = maxItems != null && rows.length >= maxItems;

  return (
    <div className="mt-1 space-y-1">
      {rows.map((row, index) => (
        <div key={index} className={buttons ? 'space-y-1' : undefined}>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={row.label || ''}
            maxLength={LINK_LABEL_MAX}
            disabled={disabled}
            placeholder="Label"
            onChange={(e) => setRow(index, { label: e.target.value }, false)}
            onBlur={onSettle}
            className="min-w-0 flex-1 px-1.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <input
            type="text"
            value={row.url || ''}
            maxLength={LINK_URL_MAX}
            disabled={disabled}
            placeholder="/where/"
            onChange={(e) => setRow(index, { url: e.target.value }, false)}
            onBlur={onSettle}
            className="min-w-0 flex-1 px-1.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <button
            type="button"
            disabled={disabled}
            title="Remove"
            onClick={() => write(rows.filter((_, i) => i !== index))}
            className="icon-btn px-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
          >
            ×
          </button>
        </div>
        {buttons && (
          // Order IS the design here — the buttons draw left to right in this
          // order, so reordering has to be possible without deleting and
          // retyping a row.
          <div className="flex items-center gap-1 pl-1">
            <select
              value={row.emphasis || 'primary'}
              disabled={disabled}
              onChange={(e) => setRow(index, { emphasis: e.target.value as any })}
              className="flex-1 min-w-0 px-1.5 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              {BUTTON_EMPHASES.map((e) => (
                <option key={e.value} value={e.value}>
                  {e.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={disabled || index === 0}
              title="Move left"
              onClick={() => move(index, -1)}
              className="px-1 text-xs text-gray-400 hover:text-blue-600 disabled:opacity-20"
            >
              ←
            </button>
            <button
              type="button"
              disabled={disabled || index === rows.length - 1}
              title="Move right"
              onClick={() => move(index, 1)}
              className="px-1 text-xs text-gray-400 hover:text-blue-600 disabled:opacity-20"
            >
              →
            </button>
          </div>
        )}
        </div>
      ))}
      <button
        type="button"
        disabled={disabled || full}
        onClick={() =>
          write([...rows, buttons ? newButton(rows.length) : newLink()])
        }
        className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline"
      >
        {full
          ? `${maxItems} is the most`
          : buttons
            ? '+ Add a button'
            : '+ Add a link'}
      </button>
    </div>
  );
}

/**
 * A swatch, a native picker and the hex, together.
 *
 * The text box matters as much as the picker: people paste hex codes out of a
 * brand document far more often than they hunt for a colour by eye, and a
 * native colour input cannot be pasted into. It also cannot express "empty",
 * which three of these fields need — so clearing is its own button.
 */
function ColourInput({
  value,
  emptyLabel,
  disabled,
  onChange,
  onSettle,
}: {
  value: any;
  emptyLabel?: string;
  disabled?: boolean;
  onChange: (raw: string) => void;
  onSettle: () => void;
}) {
  const hex = typeof value === 'string' ? value : '';
  const valid = isHex(hex);
  return (
    <div className="flex items-center gap-1">
      <input
        type="color"
        value={valid ? hex : '#ffffff'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onSettle}
        className="h-6 w-8 rounded border border-gray-300 dark:border-gray-600 bg-transparent p-0"
        title={valid ? hex : emptyLabel || 'Pick a color'}
      />
      <input
        type="text"
        value={hex}
        placeholder={emptyLabel || '#000000'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onSettle}
        className={`w-24 px-1.5 py-1 text-xs font-mono rounded border bg-white dark:bg-gray-800 ${
          hex && !valid
            ? 'border-amber-400 text-amber-700 dark:text-amber-400'
            : 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100'
        }`}
      />
      {emptyLabel && (
        <button
          type="button"
          disabled={disabled || !hex}
          onClick={() => {
            onChange('');
            setTimeout(onSettle, 0);
          }}
          title={`Clear - use ${emptyLabel.toLowerCase()}`}
          className="icon-btn px-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
        >
          ×
        </button>
      )}
      {valid && (
        <span
          aria-hidden
          className="w-3 h-3 rounded-sm border border-gray-300 dark:border-gray-600"
          style={{
            background: hex,
            // A near-white swatch on a white panel is invisible; the border
            // above is what shows it, and the shadow keeps a dark one readable.
            boxShadow: luminance(hex) > 0.9 ? 'inset 0 0 0 1px rgba(0,0,0,.15)' : undefined,
          }}
        />
      )}
    </div>
  );
}
